import {
  DUPE_API_URL,
  FeatureFlags,
  UploadFileTypes,
} from '@dupe/common/constants'
import { processSheetProducts } from '@dupe/common/helpers'
import { useRouter } from 'next/router'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { DropzoneInputProps, FileRejection, useDropzone } from 'react-dropzone'
import { FormProvider, useForm } from 'react-hook-form'
import { useAuthContext } from '~/src/context/AuthProvider'
import useAuthUser from '~/src/hooks/useAuthUser'
import useInIframe from '~/src/hooks/useInIframe'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import fetcher from '~/src/lib/fetcher'
import logger from '~/src/lib/logger'
import { uploadImageViaAPI } from '~/src/lib/uploadImageAPI'

type SearchState = {
  query?: string
  searching?: boolean
  refresh?: boolean
  source?: string
  method?: string
}

export type SearchDataInput = {
  q?: string
  compare: 'on' | 'off'
}

export const MAX_FILE_SIZE = 20000000 // 20mb
export const FILE_SIZE_ERROR_MESSAGE = 'File size must be less than 20mb'

interface ISearchContext {
  fileData: string | undefined
  searchState: SearchState
  setSearchState: React.Dispatch<React.SetStateAction<SearchState>>
  processing?: boolean
  redirecting?: boolean
  isDragActive?: boolean
  getInputProps: <T extends DropzoneInputProps>(props?: T | undefined) => T
  uploadedFileResult?: string
  uploadClickType?: 'image' | 'document' | undefined
  setUploadClickType: React.Dispatch<
    React.SetStateAction<UploadFileTypes | undefined>
  >
  resetProcessing: () => void
  setSourceType: (source: string) => void
  sourceType: string | undefined
  hasInteracted: boolean
  setHasInteracted: React.Dispatch<React.SetStateAction<boolean>>
  handleImageUpload: (
    fileData: string,
    enableVision?: boolean
  ) => Promise<{
    hash: string
    imageUrl: string
    parsedData?: { category: string; description: string }
  }>
}

const defaultContext: ISearchContext = {
  fileData: undefined,
  searchState: {
    searching: false,
  },
  setSearchState: () => {},
  processing: false,
  redirecting: false,
  isDragActive: false,
  getInputProps: () => {
    return {} as any
  },
  uploadedFileResult: undefined,
  uploadClickType: undefined,
  setUploadClickType: () => {},
  resetProcessing: () => {},
  setSourceType: () => {},
  sourceType: undefined,
  hasInteracted: false,
  setHasInteracted: () => {},
  handleImageUpload: async () => {
    throw new Error('handleImageUpload not implemented')
  },
}

const SearchContext = createContext<ISearchContext>(defaultContext)

export const useSearchContext = () => {
  const context = useContext(SearchContext)

  if (context === undefined) {
    throw new Error('useSearchContext() must be used within an SearchProvider')
  }

  return context
}

interface Props extends React.PropsWithChildren {}

export const SearchProvider: React.FC<Props> = ({ children }) => {
  const router = useRouter()
  const { inIframe } = useInIframe()
  const [fileData, setFileData] = useState<string>()
  const [processing, setProcessing] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [uploadClickType, setUploadClickType] = useState<UploadFileTypes>()
  const [uploadedFileResult, setUploadedFileResult] = useState<string>()
  const [searchState, setSearchState] = useState<SearchState>(
    defaultContext.searchState
  )
  const [sourceType, setSourceType] = useState<string>()

  const isRestyle = sourceType === 'dupe-input-restyle'

  const uploadResultRef = useRef<{
    fileData: string
    hash?: string
    imageUrl?: string
    wasAuthenticatedOnDrop?: boolean
  } | null>(null)

  const { openAuthModal } = useAuthContext()
  const { authUser } = useAuthUser()

  const { featureOn: sheetFileSupported } = useIsFeatureOn(
    FeatureFlags.SupportsSheetCsvFiles
  )

  const formMethods = useForm<SearchDataInput>({
    mode: 'onSubmit',
    defaultValues: {
      compare: 'on',
    },
  })

  let modalTitle: React.ReactNode = 'Sign up to see dupes of this product'
  let modalSubtitle = 'This is an advanced feature and requires an account'

  if (isRestyle) {
    modalTitle = (
      <>
        Sign up <br />
        to restyle this room
      </>
    )
  }

  const enqueueUploadProcessing = useCallback(
    async (urls: string[]) => {
      try {
        const enqueueResp = await fetcher<{
          results: any
          bulkId: string
        }>(`${DUPE_API_URL}/api/enqueue-urls`, {
          method: 'POST',
          authUser,
          dataOrParams: {
            urls,
          },
        })

        const savedBulkId = enqueueResp?.bulkId
        const redirectPath = `/profile/s/${savedBulkId}`
        if (inIframe) {
          window.parent.location.assign(redirectPath)
        } else {
          window.location.assign(redirectPath)
        }
      } catch (e) {
        alert('There was an error processing the file')
      } finally {
        setProcessing(false)
      }
    },
    [authUser, inIframe, setProcessing]
  )

  const uploadSheet = useCallback(
    async (file: File) => {
      try {
        const supportedTypes = [
          'text/csv',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/plain',
          'spreadsheetml',
          'ms-excel',
          'csv',
        ]

        // check if file type contains a supported type - we use includes to include partial matches on each type from the array above
        if (!supportedTypes.some((type) => file.type.includes(type))) {
          return alert('Please select a CSV or Excel file')
        }

        // file size limit
        if (file.size > MAX_FILE_SIZE) {
          alert(FILE_SIZE_ERROR_MESSAGE)
          return
        }

        const fileBuffer = await file.arrayBuffer()
        const buffer = Buffer.from(fileBuffer)
        const contentType = file.type

        setProcessing(true)

        const urls = await processSheetProducts(buffer, contentType)

        if (typeof urls === 'undefined') {
          return alert('There was an error processing the file')
        }

        if (!authUser?.uid) {
          openAuthModal({
            modalTitle,
            modalSubtitle,
            source: sourceType || 'dupe-input-dropzone',
            callback: () => {
              enqueueUploadProcessing(urls)
            },
          })
        } else {
          enqueueUploadProcessing(urls)
        }
      } catch (e) {
        alert('There was an error processing the file')
      } finally {
        setProcessing(false)
      }
    },
    [
      authUser?.uid,
      openAuthModal,
      modalTitle,
      modalSubtitle,
      sourceType,
      enqueueUploadProcessing,
    ]
  )

  const redirectToResults = useCallback(
    (hash: string, imageUrl: string) => {
      setRedirecting(true)
      if (inIframe) {
        window.parent.location.assign(`/results/${hash}?url=${imageUrl}`)
      } else {
        window.location.assign(`/results/${hash}?url=${imageUrl}`)
      }
    },
    [inIframe]
  )

  const redirectToRestyle = useCallback(
    (hash: string, imageUrl: string) => {
      setRedirecting(true)
      if (inIframe) {
        window.parent.location.assign(`/restyle/${hash}?url=${imageUrl}`)
      } else {
        window.location.assign(`/restyle/${hash}?url=${imageUrl}`)
      }
    },
    [inIframe]
  )

  const handleImageUpload = useCallback(
    async (fileData: string, enableVision: boolean = false) => {
      setProcessing(true)

      try {
        // Use the new API endpoint for better consolidation and optional vision processing
        const result = await uploadImageViaAPI(fileData, {
          enableVisionProcessing: enableVision,
          authUser,
        })

        return {
          hash: result.hash,
          imageUrl: result.imageUrl,
          parsedData: result.parsedData,
        }
      } catch (error) {
        logger.error('Error uploading image', { error })
        throw error
      } finally {
        setProcessing(false)
      }
    },
    [authUser]
  )

  const handleUploadAndRedirect = useCallback(
    async (fileData: string) => {
      try {
        const { hash, imageUrl } = await handleImageUpload(fileData)
        setUploadedFileResult(imageUrl)

        if (!hash) {
          alert('There was an error processing the image')
          logger.error('Error uploading and redirecting', {
            fileData,
            imageUrl,
          })
          return
        }

        if (isRestyle) {
          redirectToRestyle(hash, imageUrl)
        } else {
          redirectToResults(hash, imageUrl)
        }
      } catch (error) {
        logger.error('Error uploading and redirecting', { error, fileData })
      } finally {
        uploadResultRef.current = null
      }
    },
    [handleImageUpload, isRestyle, redirectToRestyle, redirectToResults]
  )

  const handleOnDrop = useCallback(
    (acceptedFiles: File[], fileRejection: FileRejection[]) => {
      // This callback handles all files that are dropped or picked - we split this into two separate functions
      // if the file is an image we process below and send to the handleUploadAndRedirect function
      // if the file is a document we send to the uploadSheet function
      try {
        const file = acceptedFiles?.[0]

        const fileSizeIssue =
          fileRejection?.[0]?.errors?.[0]?.code === 'file-too-large'

        if (fileSizeIssue) {
          alert(FILE_SIZE_ERROR_MESSAGE)
          return
        }

        if (!file) {
          const errorMessage = sheetFileSupported
            ? 'Please select a file'
            : 'Please select an image'

          alert(errorMessage)
          return
        }

        // if not an image split out into a separate function
        if (sheetFileSupported && !file.type.startsWith('image/')) {
          setUploadClickType(UploadFileTypes.DOCUMENT)
          setProcessing(true)
          return uploadSheet(file)
        }

        // check if image
        if (!file.type.startsWith('image/')) {
          alert('Please select an image')
          return
        }

        // file size limit
        if (file.size > MAX_FILE_SIZE) {
          alert(FILE_SIZE_ERROR_MESSAGE)
          return
        }

        setProcessing(true)

        // If image ensure we show processing indicator even if dropped in the dropzone
        setUploadClickType(UploadFileTypes.IMAGE)

        const reader = new FileReader()
        reader.onload = async (e) => {
          if (e.target?.result) {
            setProcessing(true)

            const fileData = e.target?.result as string

            // Track if user was authenticated when file was dropped
            uploadResultRef.current = {
              fileData,
              wasAuthenticatedOnDrop: !!authUser?.uid,
            }

            // Add artificial delay to show loading happening before auth modal is shown
            await new Promise((resolve) => setTimeout(resolve, 1000))
            setFileData(fileData)

            // If user is not logged in, show auth modal with preview
            if (!authUser?.uid) {
              openAuthModal({
                modalTitle,
                modalSubtitle,
                modalAsset: fileData,
                source: sourceType || 'dupe-input-dropzone',
                callback: async () => {
                  setProcessing(true)

                  // Check if upload already completed during auth
                  if (uploadResultRef.current?.hash) {
                    if (isRestyle) {
                      redirectToRestyle(
                        uploadResultRef.current.hash,
                        uploadResultRef.current.imageUrl!
                      )
                    } else {
                      redirectToResults(
                        uploadResultRef.current.hash,
                        uploadResultRef.current.imageUrl!
                      )
                    }
                  } else {
                    // If not, upload immediately and redirect to results
                    await handleUploadAndRedirect(fileData)
                  }
                },
              })
            } else {
              // User is already logged in, proceed with upload immediately
              await handleUploadAndRedirect(fileData)
            }
          }
        }

        reader.readAsDataURL(file)
      } catch (e) {
        logger.info('Error handling file drop', { error: e })
      }
    },
    [
      sheetFileSupported,
      uploadSheet,
      authUser?.uid,
      sourceType,
      openAuthModal,
      modalTitle,
      modalSubtitle,
      isRestyle,
      redirectToRestyle,
      redirectToResults,
      handleUploadAndRedirect,
    ]
  )

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejection: FileRejection[]) => {
      handleOnDrop(acceptedFiles, fileRejection)
    },
    [handleOnDrop]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: sheetFileSupported
      ? {
          // images
          'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
          // csv or excel documents
          'application/vnd.ms-excel': ['.csv'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
            '.xlsx',
          ],
        }
      : {
          // images
          'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
        },
    noClick: true,
    multiple: false,
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
    preventDropOnDocument: true,
    // Disable dropzone on trade pages
    disabled: router.pathname.startsWith('/trade'),
  })

  const resetProcessing = useCallback(() => {
    // Reset processing state
    setProcessing(false)
    setRedirecting(false)
    setUploadClickType(undefined)
    setSourceType(undefined)
    uploadResultRef.current = null

    // Reset uploaded file result
    const fileUrl = uploadedFileResult
    logger.debug('Resetting uploaded file result', { fileUrl })
    setUploadedFileResult(undefined) // TODO - send api call with url to delete from imagekit

    // Reset file input value
    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }, [setProcessing, uploadedFileResult])

  // Handle background upload during auth
  useEffect(() => {
    // Only process background uploads for users who just authenticated
    // If user was already authenticated when file was dropped, skip this
    if (!authUser?.uid || uploadResultRef.current?.wasAuthenticatedOnDrop)
      return

    // Check if we have file data to process
    if (!uploadResultRef.current?.fileData) return

    // Check if upload already completed
    if (uploadResultRef.current.hash) return

    // NOTE: this is called when user is authenticated and file is uploaded
    const processUpload = async () => {
      try {
        const { hash, imageUrl } = await handleImageUpload(
          uploadResultRef.current!.fileData
        )

        // Update ref with results while preserving fileData
        const fileData = uploadResultRef.current!.fileData
        uploadResultRef.current = {
          fileData,
          hash,
          imageUrl,
        }
        setUploadedFileResult(imageUrl)
      } catch (error) {
        logger.error('Error processing background upload', { error })
        // Don't show alert here since this is a background process
      }
    }

    processUpload()
  }, [authUser?.uid, handleImageUpload])

  // if queryParam is present, set it on searchState
  // (this is to handle the /search?q=foo)
  useEffect(() => {
    if (!router.isReady) return

    const pathname = router.pathname
    const queryParam = router.query.q as string

    const validPath = ['/search']
    const isValidPath = validPath.includes(pathname)
    if (isValidPath && !!queryParam) {
      setSearchState({
        query: queryParam,
        searching: true,
        source: 'embed',
        method: 'queryParam',
      })
    }
  }, [router, setSearchState])

  return (
    <SearchContext.Provider
      value={{
        fileData,
        searchState,
        setSearchState,
        processing,
        redirecting,
        isDragActive,
        getInputProps,
        uploadedFileResult,
        uploadClickType,
        setUploadClickType,
        resetProcessing,
        setSourceType,
        sourceType,
        hasInteracted,
        setHasInteracted,
        handleImageUpload,
      }}
    >
      <FormProvider {...formMethods}>
        <div {...getRootProps()}>{children}</div>
      </FormProvider>
    </SearchContext.Provider>
  )
}
